import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, ChapterLabel } from "./Reveal";

export default function ForParents() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="parents" data-testid="parents-section" className="relative py-16 sm:py-24">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <ChapterLabel number="08" label="For Parents" />
        <Reveal delay={0.1}>
          <h2
            data-testid="parents-heading"
            className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#FFFDF9]"
          >
            For parents.
            <br />
            See <span className="italic text-[#38BDF8]">more</span> than the marks.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
            AuraXR could create a clearer connection between what happens in the classroom and what
            parents understand at home.
          </p>
        </Reveal>

        <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <Reveal delay={0.15}>
            <div ref={imgRef} className="relative overflow-hidden rounded-2xl border border-white/[0.08]">
              <motion.img
                src="/images/parents.jpg"
                alt="Happy Indian parents reviewing their child's learning summary together"
                loading="lazy"
                style={{ y }}
                className="aspect-[4/3] w-full object-cover scale-[1.12]"
              />
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div
              data-testid="parent-concept-panel"
              className="rounded-2xl border border-white/[0.09] bg-[#0B1120]/80 p-7 sm:p-9 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.35em] uppercase text-[#38BDF8]">
                  Concept interface
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8]" />
              </div>

              <p className="mt-6 font-display text-xl text-[#FFFDF9]">Aarav — Science</p>
              <p className="mt-1 text-xs text-slate-400 tracking-wide">Concept understanding</p>

              <div className="mt-5 flex items-end gap-4">
                <span data-testid="parent-score" className="font-display text-5xl text-[#FFFDF9]">
                  82<span className="text-xl text-[#38BDF8]">%</span>
                </span>
                <div className="mb-2.5 h-1.5 flex-1 rounded-full bg-white/[0.08] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "82%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
                    className="h-full rounded-full bg-[#38BDF8]"
                  />
                </div>
              </div>

              <div className="mt-7 grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400">Doing well</p>
                  <ul className="mt-3 space-y-2">
                    {["Human Body", "Matter"].map((t) => (
                      <li key={t} className="flex items-center gap-2.5 text-sm text-slate-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400">
                    Needs more support
                  </p>
                  <ul className="mt-3 space-y-2">
                    {["Electricity", "Force & Motion"].map((t) => (
                      <li key={t} className="flex items-center gap-2.5 text-sm text-slate-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-7 border-t border-white/[0.07] pt-4 text-[11px] text-slate-500 leading-relaxed">
                Example data — not actual AuraXR performance
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
